import { apiClient } from './client';
import { auth } from '../firebase/config';

const getAuthHeaders = async () => {
  console.log('🔑 Dashboard API: Getting auth headers...');
  try {
    // Try to get fresh token from Firebase
    const user = auth.currentUser;
    if (user) {
      console.log('👤 Dashboard API: Current user found:', user.email);
      const token = await user.getIdToken(true);
      console.log('✅ Dashboard API: Fresh token obtained, length:', token.length);
      return { Authorization: `Bearer ${token}` };
    }
    
    // Fallback to stored token
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      console.log('💾 Dashboard API: Using stored token, length:', storedToken.length);
      return { Authorization: `Bearer ${storedToken}` };
    }
    
    console.warn('⚠️ Dashboard API: No auth token available');
    return {};
  } catch (error) {
    console.error('❌ Dashboard API: Failed to get auth token:', error);
    // Try stored token as last resort
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      console.log('💾 Dashboard API: Using stored token as fallback');
      return { Authorization: `Bearer ${storedToken}` };
    }
    return {};
  }
};

// Admin Dashboard APIs
export const getAdminSummary = async () => {
  console.log('📈 Dashboard API: Fetching admin summary...');
  try {
    const headers = await getAuthHeaders();
    console.log('🔄 Dashboard API: Making request to /admin/summary');
    const response = await apiClient.get('/admin/summary', { headers });
    console.log('✅ Dashboard API: Admin summary received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Dashboard API: Admin summary failed:', error.response?.status, error.message);
    throw error;
  }
};

export const getAdminPerformanceDistribution = async () => {
  console.log('📈 Dashboard API: Fetching performance distribution...');
  try {
    const headers = await getAuthHeaders();
    const response = await apiClient.get('/admin/performance-distribution', { headers });
    console.log('✅ Dashboard API: Performance distribution received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Dashboard API: Performance distribution failed:', error.response?.status, error.message);
    throw error;
  }
};

export const getAdminTrends = async (days = 7) => {
  console.log('📈 Dashboard API: Fetching trends for', days, 'days...');
  try {
    const headers = await getAuthHeaders();
    const response = await apiClient.get(`/admin/trends?days=${days}`, { headers });
    console.log('✅ Dashboard API: Trends received:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Dashboard API: Trends failed:', error.response?.status, error.message);
    throw error;
  }
};

// HR Dashboard APIs
export const getHrSummary = async () => {
  console.log('📊 Dashboard API: Fetching HR summary');
  const headers = await getAuthHeaders();
  const response = await apiClient.get('/dashboard/hr/summary', { headers });
  console.log('✅ Dashboard API: HR summary received');
  return response.data;
};

export const getHrTopPerformers = async (limit = 10) => {
  console.log('📊 Dashboard API: Fetching top performers', { limit });
  const headers = await getAuthHeaders();
  const response = await apiClient.get(`/dashboard/hr/top-performers?limit=${limit}`, { headers });
  console.log('✅ Dashboard API: Top performers received');
  return response.data;
};

export const getHrRiskSignals = async () => {
  console.log('📊 Dashboard API: Fetching risk signals');
  const headers = await getAuthHeaders();
  const response = await apiClient.get('/dashboard/hr/risk-signals', { headers });
  console.log('✅ Dashboard API: Risk signals received');
  return response.data;
};

// Performance APIs
export const getSystemHealth = async () => {
  const headers = await getAuthHeaders();
  const response = await apiClient.get('/performance/health', { headers });
  return response.data;
};

export const getSystemMetrics = async () => {
  const headers = await getAuthHeaders();
  const response = await apiClient.get('/performance/metrics', { headers });
  return response.data;
};

// Admin Organization APIs
export const getCustomers = async () => {
  const headers = await getAuthHeaders();
  const response = await apiClient.get('/admin/customers', { headers });
  return response.data;
};

export const getCustomerSummary = async (customerId) => {
  const headers = await getAuthHeaders();
  const response = await apiClient.get(`/admin/customer/${customerId}/summary`, { headers });
  return response.data;
};

// Customer Dashboard API
export const getCustomerDashboard = async (customerId = null) => {
  console.log('📊 Dashboard API: Fetching customer dashboard', { customerId });
  const headers = await getAuthHeaders();
  const url = customerId ? `/customer/dashboard?customerId=${customerId}` : '/customer/dashboard';
  const response = await apiClient.get(url, { headers });
  console.log('✅ Dashboard API: Customer dashboard received');
  return response.data;
};

// Enhanced Admin Dashboard APIs
export const getAssessmentBreakdown = async () => {
  const headers = await getAuthHeaders();
  const response = await apiClient.get('/admin/assessment-breakdown', { headers });
  return response.data;
};